'use client';

import { FC, useState } from 'react';
import { isFilled } from '@prismicio/client';
import type { RichTextField, ImageField } from '@prismicio/client';
import { PrismicRichText } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';

/**
 * Dark "What The Calculator Gives You" grid — eyebrow pill, centered title, and
 * a 2x2 grid of icon + title + description cards over a background image
 * (aurora/light-streaks). One cell is highlighted by default and the highlight
 * follows the cursor on hover. Mirrors Figma node 18252:1569.
 */

type CardItem = {
  icon?: ImageField;
  title?: string;
  description?: string;
  is_highlighted?: boolean;
};

type LandingFeatureCardGridPrimary = {
  transparent_bg?: boolean;
  background_image?: ImageField;
  eyebrow?: string;
  title?: RichTextField;
  body?: RichTextField;
  cards?: CardItem[];
};

type LandingFeatureCardGridSlice = {
  slice_type: string;
  variation: string;
  primary: LandingFeatureCardGridPrimary;
};

// Visible lavender dividers that fade out at the edges (matches the design).
const V_FADE =
  'linear-gradient(to bottom, transparent 0%, rgba(168,135,255,0.45) 18%, rgba(168,135,255,0.45) 82%, transparent 100%)';
const H_FADE =
  'linear-gradient(to right, transparent 0%, rgba(168,135,255,0.45) 18%, rgba(168,135,255,0.45) 82%, transparent 100%)';
const HIGHLIGHT_BG =
  'linear-gradient(180deg, rgba(168,135,255,0.16) 0%, rgba(168,135,255,0.04) 100%)';

const LandingFeatureCardGrid: FC<{ slice: LandingFeatureCardGridSlice }> = ({
  slice,
}) => {
  const { transparent_bg, background_image, eyebrow, title, body, cards } =
    slice.primary;
  const transparent = Boolean(transparent_bg);
  const items = (cards ?? []).filter(
    (c) => c?.title || c?.description || isFilled.image(c?.icon),
  );

  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const defaultIndex = Math.max(
    items.findIndex((c) => c.is_highlighted),
    items.length > 1 ? 1 : 0,
  );
  const isHighlighted = (i: number) =>
    hoveredIndex !== null ? hoveredIndex === i : i === defaultIndex;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={`relative mx-[30px] overflow-hidden rounded-[24px] py-16 md:py-24 ${
        transparent ? 'bg-transparent' : 'bg-[#020a10]'
      }`}
    >
      {/* Background: image when set, otherwise an approximated aurora glow. */}
      {!transparent &&
        (isFilled.image(background_image) ? (
          <PrismicNextImage
            field={background_image}
            fill
            sizes="100vw"
            className="pointer-events-none absolute inset-0 h-full w-full object-cover"
            fallbackAlt=""
          />
        ) : (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{
              backgroundImage:
                'radial-gradient(70% 50% at 85% 0%, rgba(75,15,234,0.22) 0%, rgba(75,15,234,0) 60%), radial-gradient(60% 40% at 10% 80%, rgba(168,135,255,0.12) 0%, rgba(168,135,255,0) 60%)',
            }}
          />
        ))}

      <div className="relative z-10 mx-auto flex w-full max-w-[1200px] flex-col items-center gap-[40px] px-4 md:px-8">
        {/* Header */}
        <div className="flex flex-col items-center gap-[20px] text-center">
          {eyebrow && (
            <span className="inline-block rounded-full border border-[#00f557] px-[16px] py-[8px] text-[12px] font-normal uppercase leading-[1.2] tracking-[0.08em] text-white md:text-[13px]">
              {eyebrow}
            </span>
          )}
          {isFilled.richText(title) && (
            <PrismicRichText
              field={title}
              components={{
                heading2: ({ children }) => (
                  <h2 className="max-w-[820px] text-balance text-[32px] font-bold leading-[1.15] text-white md:text-[42px]">
                    {children}
                  </h2>
                ),
              }}
            />
          )}
          {isFilled.richText(body) && (
            <div className="max-w-[760px]">
              <PrismicRichText
                field={body}
                components={{
                  paragraph: ({ children }) => (
                    <p className="text-[14px] leading-[1.6] text-white/70 md:text-[15px] [&:not(:first-child)]:mt-3">
                      {children}
                    </p>
                  ),
                  strong: ({ children }) => (
                    <strong className="font-semibold text-[#00f557]">
                      {children}
                    </strong>
                  ),
                }}
              />
            </div>
          )}
        </div>

        {/* 2x2 grid */}
        {items.length > 0 && (
          <div className="relative mx-auto w-full max-w-[920px] overflow-hidden rounded-[16px]">
            <div className="grid auto-rows-fr grid-cols-1 sm:grid-cols-2">
              {items.map((card, i) => (
                <div
                  key={i}
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  className="flex cursor-default flex-col items-center gap-[10px] px-[24px] py-[40px] text-center transition-[background] duration-300"
                  style={{
                    background: isHighlighted(i) ? HIGHLIGHT_BG : undefined,
                  }}
                >
                  {isFilled.image(card.icon) && (
                    <div className="mb-[6px] h-[44px] w-[44px] shrink-0">
                      <PrismicNextImage
                        field={card.icon}
                        className="h-full w-full object-contain"
                        fallbackAlt=""
                      />
                    </div>
                  )}
                  {card.title && (
                    <h3 className="max-w-[300px] text-[18px] font-semibold leading-[1.3] text-white md:text-[20px]">
                      {card.title}
                    </h3>
                  )}
                  {card.description && (
                    <p className="max-w-[320px] text-[13px] leading-[1.5] text-white/65 md:text-[14px]">
                      {card.description}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {/* Hairline dividers (sm+): vertical + horizontal through the centre
                of the 2x2 grid, fading out at the edges. */}
            <div
              className="pointer-events-none absolute inset-y-0 hidden w-px sm:block"
              style={{ left: '50%', background: V_FADE }}
            />
            {items.length > 2 && (
              <div
                className="pointer-events-none absolute inset-x-0 hidden h-px sm:block"
                style={{ top: '50%', background: H_FADE }}
              />
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default LandingFeatureCardGrid;
